import React, { createContext, useContext, useState, useCallback } from 'react';
import * as accountApi from '../utils/accountApi';
import { sendInviteEmail } from '../utils/outreachApi';
import { useAuth } from './AuthContext';

const OrgContext = createContext(null);

export function useOrg() {
    const ctx = useContext(OrgContext);
    if (!ctx) throw new Error('useOrg must be used within OrgProvider');
    return ctx;
}

export function OrgProvider({ orgId, children }) {
    const { user } = useAuth();
    const [org, setOrg] = useState(null);
    const [members, setMembers] = useState([]);
    const [credentials, setCredentials] = useState([]);
    const [serviceConfigs, setServiceConfigs] = useState([]);
    const [systemDefaults, setSystemDefaults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const loadOrg = useCallback(async () => {
        if (!orgId) return;
        try {
            setLoading(true);
            setError('');
            const data = await accountApi.getOrganization(orgId);
            setOrg(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [orgId]);

    const saveOrg = useCallback(async (updates) => {
        if (!orgId) return;
        try {
            setLoading(true);
            setError('');
            const data = await accountApi.updateOrganization(orgId, updates);
            setOrg(data);
            return data;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [orgId]);

    const loadMembers = useCallback(async () => {
        if (!orgId) return;
        try {
            setLoading(true);
            setError('');
            const data = await accountApi.listMembers(orgId);
            setMembers(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [orgId]);

    const updateRole = useCallback(async (userId, role) => {
        if (!orgId) return;
        try {
            setError('');
            await accountApi.updateMemberRole(orgId, userId, role);
            setMembers(prev => prev.map(m => m.user_id === userId ? { ...m, role } : m));
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, [orgId]);

    const updateMember = useCallback(async (userId, updates) => {
        if (!orgId) return;
        try {
            setError('');
            const updated = await accountApi.updateUser(userId, updates);
            // Only reflect the fields we edited. The PATCH response carries the
            // platform `role` (author/admin/superuser), which is NOT the org
            // membership role shown in the table — merging it wholesale would
            // clobber the role badge, so apply name/email only.
            setMembers(prev => prev.map(m => {
                if (m.user_id !== userId) return m;
                const next = { ...m };
                if ('name' in updates) next.name = (updated && updated.name != null) ? updated.name : updates.name;
                if ('email' in updates) next.email = (updated && updated.email) ? updated.email : updates.email;
                return next;
            }));
            return updated;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, [orgId]);

    const removeMember = useCallback(async (userId) => {
        if (!orgId) return;
        try {
            setError('');
            await accountApi.removeMember(orgId, userId);
            setMembers(prev => prev.filter(m => m.user_id !== userId));
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, [orgId]);

    // ─── Credentials ───────────────────────────────────────────

    const loadCredentials = useCallback(async () => {
        if (!orgId) return;
        try {
            setError('');
            const data = await accountApi.listCredentials(orgId);
            setCredentials(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err.message);
        }
    }, [orgId]);

    const addCredential = useCallback(async (payload) => {
        if (!orgId) return;
        try {
            setError('');
            const created = await accountApi.createCredential(orgId, payload);
            setCredentials(prev => [...prev, created]);
            return created;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, [orgId]);

    const removeCredential = useCallback(async (credentialId) => {
        if (!orgId) return;
        try {
            setError('');
            await accountApi.deleteCredential(orgId, credentialId);
            setCredentials(prev => prev.filter(c => c.credential_id !== credentialId));
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, [orgId]);

    // ─── Service Configs ───────────────────────────────────────

    const loadServiceConfigs = useCallback(async () => {
        if (!orgId) return;
        try {
            setError('');
            const data = await accountApi.listServiceConfigs(orgId);
            setServiceConfigs(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err.message);
        }
    }, [orgId]);

    const saveServiceConfig = useCallback(async (serviceType, payload) => {
        if (!orgId) return;
        try {
            setError('');
            const result = await accountApi.upsertServiceConfig(orgId, serviceType, payload);
            setServiceConfigs(prev => {
                const idx = prev.findIndex(c => c.service_type === serviceType);
                if (idx >= 0) {
                    const next = [...prev];
                    next[idx] = result;
                    return next;
                }
                return [...prev, result];
            });
            return result;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, [orgId]);

    const removeServiceConfig = useCallback(async (serviceType) => {
        if (!orgId) return;
        try {
            setError('');
            await accountApi.deleteServiceConfig(orgId, serviceType);
            setServiceConfigs(prev => prev.filter(c => c.service_type !== serviceType));
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, [orgId]);

    // ─── Logo ───────────────────────────────────────────────────

    const uploadLogo = useCallback(async (file) => {
        if (!orgId) return;
        try {
            setLoading(true);
            setError('');
            const updatedOrg = await accountApi.uploadLogo(orgId, file);
            setOrg(updatedOrg);
            return updatedOrg;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [orgId]);

    const deleteLogo = useCallback(async () => {
        if (!orgId) return;
        try {
            setLoading(true);
            setError('');
            await accountApi.deleteLogo(orgId);
            setOrg(prev => prev ? { ...prev, logo_url: '' } : prev);
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [orgId]);

    // ─── Invite Member ─────────────────────────────────────────

    const inviteMember = useCallback(async (email, role = 'member', name) => {
        if (!orgId) return;
        try {
            setLoading(true);
            setError('');
            const result = await accountApi.inviteMember(orgId, email, role, name);
            const inviteToken = result?.invite_token;

            // If no invite token, rollback — the user cannot onboard without it
            if (!inviteToken) {
                console.error('[INVITE_FAILED]', email, 'No invite token returned');
                if (result?.user_id) {
                    try { await accountApi.removeMember(orgId, result.user_id); } catch (_) { }
                }
                throw new Error(`Invitation failed — no invite token was generated for ${email}.`);
            }

            // Send invite email — if it fails, rollback the user
            try {
                await sendInviteEmail({
                    email,
                    orgName: org?.name,
                    adminName: user?.name || '',
                    adminEmail: user?.email || '',
                    inviteToken,
                });
            } catch (emailErr) {
                console.error('[INVITE_FAILED]', email, emailErr.message);
                try { await accountApi.removeMember(orgId, result.user_id); } catch (_) { }
                throw new Error(`Invitation failed — the email to ${email} could not be sent.`);
            }

            // Success — reload members list
            await loadMembers();
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [orgId, org, user, loadMembers]);

    // ─── System Defaults ───────────────────────────────────────

    const loadSystemDefaults = useCallback(async () => {
        try {
            setError('');
            const data = await accountApi.listSystemDefaults();
            setSystemDefaults(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err.message);
        }
    }, []);

    return (
        <OrgContext.Provider value={{
            orgId, org, members, credentials, serviceConfigs, systemDefaults,
            loading, error, setError,
            loadOrg, saveOrg,
            loadMembers, updateRole, updateMember, removeMember, inviteMember,
            loadCredentials, addCredential, removeCredential,
            loadServiceConfigs, saveServiceConfig, removeServiceConfig,
            loadSystemDefaults,
            uploadLogo, deleteLogo,
        }}>
            {children}
        </OrgContext.Provider>
    );
}

